import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '@/lib/api-client';
import { toast } from '@/utils/toast';
import type {
  ClientPricingListResponse,
  ClientPricingDetail,
  PricingUpdateRequest,
  PricingListFilters,
} from './types';
import { DEFAULT_PRICING_FILTERS } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRICING_KEY = 'pricing';
const PRICING_DETAIL_KEY = 'pricing-detail';
const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Build query params from filter state
// ---------------------------------------------------------------------------

function buildParams(filters: PricingListFilters, debouncedSearch: string): Record<string, string> {
  const params: Record<string, string> = {
    page: String(filters.page),
    page_size: String(filters.page_size),
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
  };
  if (debouncedSearch) params.search = debouncedSearch;
  return params;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UsePricingReturn {
  // List
  data: ClientPricingListResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  filters: PricingListFilters;
  setSearch: (value: string) => void;
  setSorting: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refresh: () => void;

  // Detail / edit
  selectedClientId: string | null;
  /** Rail whose row was clicked — the one the editor opens on. */
  selectedServiceCode: string | null;
  pricingDetail: ClientPricingDetail | undefined;
  detailLoading: boolean;
  detailError: Error | null;
  openEditor: (clientId: string, serviceCode: string) => void;
  closeEditor: () => void;
  updatePricing: (clientId: string, payload: PricingUpdateRequest) => Promise<void>;
  isUpdating: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePricing(): UsePricingReturn {
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<PricingListFilters>(DEFAULT_PRICING_FILTERS);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedServiceCode, setSelectedServiceCode] = useState<string | null>(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [filters.search]);

  const params = buildParams(filters, debouncedSearch);

  // List query
  const listQuery = useQuery<ClientPricingListResponse>({
    queryKey: [PRICING_KEY, params],
    queryFn: () => api.get<ClientPricingListResponse>('/api/pricing', { params }),
  });

  // Detail query — enabled only when a client is selected
  const detailQuery = useQuery<ClientPricingDetail>({
    queryKey: [PRICING_DETAIL_KEY, selectedClientId],
    queryFn: () => api.get<ClientPricingDetail>(`/api/clients/${selectedClientId}/pricing`),
    enabled: selectedClientId !== null,
  });

  // Update mutation
  const mutation = useMutation<
    ClientPricingDetail,
    ApiError,
    { clientId: string; payload: PricingUpdateRequest }
  >({
    mutationFn: ({ clientId, payload }) =>
      api.put<ClientPricingDetail>(`/api/clients/${clientId}/pricing`, payload),
    onSuccess: () => {
      toast.success('Pricing updated');
      setSelectedClientId(null);
      setSelectedServiceCode(null);
      // The detail query is invalidated too: the modal edits one rail, and
      // reopening the client must not show the pre-save figures for it.
      void queryClient.invalidateQueries({ queryKey: [PRICING_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PRICING_DETAIL_KEY] });
    },
    onError: (error) => {
      const message =
        typeof error.message === 'string'
          ? error.message
          : Array.isArray(error.message)
            ? error.message[0]
            : 'Failed to update pricing. Please try again.';
      toast.error(message);
    },
  });

  // Filter setters — reset page to 1 when filters change
  const setSearch = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const setSorting = useCallback((sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setFilters((prev) => ({ ...prev, page_size: size, page: 1 }));
  }, []);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [PRICING_KEY] });
  }, [queryClient]);

  const openEditor = useCallback((clientId: string, serviceCode: string) => {
    setSelectedClientId(clientId);
    setSelectedServiceCode(serviceCode);
  }, []);

  const closeEditor = useCallback(() => {
    setSelectedClientId(null);
    setSelectedServiceCode(null);
  }, []);

  const updatePricing = useCallback(
    async (clientId: string, payload: PricingUpdateRequest) => {
      await mutation.mutateAsync({ clientId, payload });
    },
    [mutation],
  );

  return {
    data: listQuery.data,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    error: listQuery.error,
    filters,
    setSearch,
    setSorting,
    setPage,
    setPageSize,
    refresh,
    selectedClientId,
    selectedServiceCode,
    pricingDetail: detailQuery.data,
    detailLoading: detailQuery.isLoading,
    detailError: detailQuery.error,
    openEditor,
    closeEditor,
    updatePricing,
    isUpdating: mutation.isPending,
  };
}
